// A popover menu. Opens from a long-press on touch, a right-click or the ⋯
// button on a desk (useMenuTrigger wires the first two); closes on outside
// press, Escape or a choice; arrows, Home and End move between items.
import { useEffect, useLayoutEffect, useRef, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';
import { cx } from './cx';

export interface MenuItem {
  label: string;
  icon?: IconName;
  /** A key hint, shown on a desk: "D". */
  kbd?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuAt { x: number; y: number }

export interface MenuProps {
  items: MenuItem[];
  /** Where to open, in viewport pixels; null keeps it closed. */
  at: MenuAt | null;
  onClose: () => void;
  label?: string;
}

export function Menu({ at, ...rest }: MenuProps) {
  if (!at) return null;
  return createPortal(<Panel at={at} {...rest} />, host());
}

/** A modal dialog makes the rest of the page inert, so a menu opened from inside one renders inside it. */
const host = (): Element => [...document.querySelectorAll('dialog[open]')].findLast((d) => d.matches(':modal')) ?? document.body;

const ITEM = '[role="menuitem"]:not([disabled])';

function Panel({ items, at, onClose, label = 'Actions' }: MenuProps & { at: MenuAt }) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; });

  // Keep the panel on screen once its size is known.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.left = `${Math.max(8, Math.min(at.x, innerWidth - r.width - 8))}px`;
    el.style.top = `${Math.max(8, Math.min(at.y, innerHeight - r.height - 8))}px`;
  }, [at]);

  // Focus moves in on open and back out on close; a press elsewhere closes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const before = document.activeElement as HTMLElement | null;
    el.querySelector<HTMLElement>(ITEM)?.focus();
    const away = (e: PointerEvent) => { if (!el.contains(e.target as Node)) close.current(); };
    document.addEventListener('pointerdown', away, true);
    return () => { document.removeEventListener('pointerdown', away, true); before?.focus(); };
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = [...(ref.current?.querySelectorAll<HTMLElement>(ITEM) ?? [])];
    const i = list.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => { e.preventDefault(); list[(n + list.length) % list.length]?.focus(); };
    if (e.key === 'ArrowDown') go(i + 1);
    else if (e.key === 'ArrowUp') go(i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(list.length - 1);
    else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); onClose(); }
  };

  return (
    <div ref={ref} className="menu" role="menu" aria-label={label} tabIndex={-1} onKeyDown={onKeyDown} style={{ left: at.x, top: at.y }}>
      {items.map((it) => (
        <button
          key={it.label} type="button" role="menuitem" className={cx('menu-item', it.danger && 'danger')} disabled={it.disabled}
          onClick={() => { onClose(); it.onSelect(); }}
        >
          {it.icon && <Icon name={it.icon} />}
          <span className="menu-label">{it.label}</span>
          {it.kbd && <kbd>{it.kbd}</kbd>}
        </button>
      ))}
    </div>
  );
}

/** Where a menu opens from a button: under its left edge. */
export const menuAnchor = (el: HTMLElement): MenuAt => {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.bottom + 4 };
};

const LONG_PRESS_MS = 450, SLOP_PX = 8;

/**
 * Handlers for the element that owns a menu: a right-click opens it under
 * the pointer, a long-press on touch opens it under the finger (and the
 * click that follows the lift is swallowed).
 */
export function useMenuTrigger(open: (at: MenuAt) => void) {
  const timer = useRef(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(0);

  // After a long-press the lift still sends mousedown, mouseup and click to
  // whatever is under the finger; mousedown would move focus off the menu
  // and click would open the row. Open beside the finger rather than under
  // it, and swallow them for a moment everywhere but on the menu itself,
  // where a press is the person choosing, however quickly.
  const fire = (x: number, y: number, touch: boolean) => {
    fired.current = performance.now();
    const swallow = (e: Event) => {
      if (e.target instanceof Element && e.target.closest('[role="menu"]')) return;
      e.stopPropagation(); e.preventDefault();
    };
    const types = ['mousedown', 'mouseup', 'click'];
    for (const t of types) document.addEventListener(t, swallow, true);
    setTimeout(() => { for (const t of types) document.removeEventListener(t, swallow, true); }, 700);
    open(touch ? { x: x + 12, y: y + 12 } : { x, y });
  };
  const cancel = () => { clearTimeout(timer.current); timer.current = 0; start.current = null; };

  return {
    onContextMenu: (e: ReactMouseEvent<HTMLElement>) => {
      e.preventDefault();
      // Android fires this after the long-press we already answered.
      if (performance.now() - fired.current < 700) return;
      fire(e.clientX, e.clientY, false);
    },
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType === 'mouse') return;
      const { clientX: x, clientY: y } = e;
      start.current = { x, y };
      timer.current = window.setTimeout(() => { timer.current = 0; fire(x, y, true); }, LONG_PRESS_MS);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const s = start.current;
      if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > SLOP_PX) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
  };
}
